import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
  useMemo,
  useCallback,
  useLayoutEffect,
} from 'react';

export interface ContextBarConfig {
  buttonLabel?: string;
  buttonUrl?: string;
  buttonOnClick?: () => void;
}

interface ContextBarContextType {
  config: ContextBarConfig | null;
  setConfig: (config: ContextBarConfig | null) => void;
}

export const ContextBarContext = createContext<ContextBarContextType>({
  config: null,
  setConfig: () => {},
});

export const useContextBar = () => {
  const context = useContext(ContextBarContext);
  if (!context) {
    throw new Error('useContextBar must be used within a ContextBarProvider');
  }
  return context;
};

/**
 * Hook to register context bar configuration for the current component.
 * Automatically clears the config when the component unmounts.
 *
 * @example
 * ```tsx
 * function NeedsList() {
 *   useContextBarConfig({
 *     buttonLabel: 'Create Need',
 *     buttonUrl: '/app/organization/needs/new'
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export const useContextBarConfig = (config: ContextBarConfig | null) => {
  const { setConfig } = useContextBar();

  // Serialize config to a string for comparison - this ensures we only update when values change
  // Exclude functions from serialization since they can't be stringified
  const configString = useMemo(() => {
    if (!config) return 'null';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { buttonOnClick, ...serializableConfig } = config;
    return JSON.stringify(serializableConfig);
  }, [config]);

  // Store current config in a ref so we always have the latest callback when we need to update
  const configRef = useRef(config);
  configRef.current = config;

  // Track previous serializable config string to detect changes
  const prevConfigStringRef = useRef<string | null>(null);
  const isMountedRef = useRef(false);

  useLayoutEffect(() => {
    const currentConfig = configRef.current;
    // Only update if serializable values changed
    // When we update, we'll use the latest callback from configRef.current
    const serializableChanged =
      !isMountedRef.current || prevConfigStringRef.current !== configString;

    if (serializableChanged) {
      prevConfigStringRef.current = configString;
      // Always pass the latest config (with latest callback) when serializable parts change
      setConfig(currentConfig);
      isMountedRef.current = true;
    }

    return () => {
      if (isMountedRef.current) {
        setConfig(null);
        prevConfigStringRef.current = null;
        isMountedRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configString, setConfig]); // Only depend on configString and setConfig
};

interface ContextBarProviderProps {
  children: ReactNode;
}

export const ContextBarProvider = ({ children }: ContextBarProviderProps) => {
  const [config, setConfigState] = useState<ContextBarConfig | null>(null);
  const prevConfigRef = useRef<ContextBarConfig | null>(null);

  // Only update state if the values actually changed
  // Compare by reference for functions, and by serialized string for other values
  const setConfig = useCallback((newConfig: ContextBarConfig | null) => {
    const prevConfig = prevConfigRef.current;

    // Check if config actually changed
    const hasChanged =
      (prevConfig === null && newConfig !== null) ||
      (prevConfig !== null && newConfig === null) ||
      (prevConfig &&
        newConfig &&
        (prevConfig.buttonLabel !== newConfig.buttonLabel ||
          prevConfig.buttonUrl !== newConfig.buttonUrl ||
          prevConfig.buttonOnClick !== newConfig.buttonOnClick));

    if (hasChanged) {
      prevConfigRef.current = newConfig;
      setConfigState(newConfig);
    }
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({ config, setConfig }), [config, setConfig]);

  return <ContextBarContext.Provider value={value}>{children}</ContextBarContext.Provider>;
};
